import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineRectangleStack,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi2";
import SharedModal from "../../components/common/SharedModal";
import {
  authService,
  plantService,
  lineService,
  userService,
} from "../../services";
import masterDataService from "../../services/masterDataService";
import { usePermissions } from "../../hooks/usePermissions";
import { toast } from "react-toastify";
import PageLoading from "../../components/common/PageLoading";
import "./Lines.css";

const Lines = () => {
  const [lines, setLines] = useState([]);
  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to show page immediately
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [plantFilter, setPlantFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [formData, setFormData] = useState({
    lineName: "",
    plantId: "",
    departmentId: "",
    lineHeadId: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("Line.View");
  const canCreate = hasPermission("Line.Create");
  const canEdit = hasPermission("Line.Edit");
  const canDelete = hasPermission("Line.Delete");

  useEffect(() => {
    if (tenantId && canView) {
      // Load data in background without blocking UI
      loadData(false);
    } else if (!canView) {
      setError("You do not have permission to view lines");
    } else {
      setError("Please login to view lines");
    }
  }, [tenantId, canView]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");

      // Load lines, plants, departments, and users in parallel
      const [linesData, plantsData, departmentsData, usersData] =
        await Promise.all([
          lineService.getAllLines(),
          plantService.getAllPlants(),
          masterDataService.getDepartments(tenantId),
          userService.getAllUsers(),
        ]);

      setLines(linesData || []);
      setPlants(plantsData || []);
      setDepartments(departmentsData || []);
      setUsers(usersData || []);

      // Pre-select default plant filter
      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setPlantFilter(plantsData[0].plantId.toString());
      }
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
      setLines([]);
      setPlants([]);
      setDepartments([]);
      setUsers([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePlantFilter = (e) => {
    const value = e.target.value;
    setPlantFilter(value);

  };

  const filteredLines = lines.filter((line) => {
    const matchesSearch = line.lineName
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesPlant =
      !plantFilter || line.plantId?.toString() === plantFilter;
    return matchesSearch && matchesPlant;
  });

  const handleAdd = () => {
    if (!canCreate) {
      setError("You do not have permission to create lines");
      return;
    }
    setEditingLine(null);

    // Get default or single plant ID
    // Auto-select plant if only 1 exists
    let initialPlantId = "";
    if (plants?.length === 1) {
      initialPlantId = plants[0].plantId.toString();
    }

    setFormData({
      lineName: "",
      plantId: initialPlantId,
      departmentId: "",
      lineHeadId: "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (line) => {
    if (!canEdit) {
      setError("You do not have permission to edit lines");
      return;
    }
    setEditingLine(line);
    setFormData({
      lineName: line.lineName || "",
      plantId: line.plantId?.toString() || "",
      departmentId: line.departmentId?.toString() || "",
      lineHeadId: line.lineHeadId?.toString() || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const toastId = toast.loading("Deleting line...");
    try {
      setError("");
      await lineService.deleteLine(id);
      await loadData(false); // Don't show loading on refresh after delete
      setDeleteConfirm(null);
      toast.update(toastId, { render: "Line deleted successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error deleting line:", err);
      console.error("Error response data:", err.response?.data);

      // Extract error message from response
      let errorMessage = "Failed to delete line. Please try again.";

      if (err.response?.data) {
        // Check for message field first
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
        // Check for errors object/array
        else if (err.response.data.errors) {
          const errors = err.response.data.errors;
          if (typeof errors === "string") {
            errorMessage = errors;
          } else if (Array.isArray(errors)) {
            errorMessage = errors.join(", ");
          } else if (typeof errors === "object") {
            // Handle object with field-specific errors
            const errorMessages = Object.values(errors).flat();
            errorMessage =
              errorMessages.length > 0
                ? errorMessages.join(", ")
                : errorMessage;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.update(toastId, { render: errorMessage, type: "error", isLoading: false, autoClose: 3000, closeButton: true });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.lineName.trim()) {
      errors.lineName = "Line name is required";
    } else if (formData.lineName.length > 100) {
      errors.lineName = "Line name must be less than 100 characters";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editingLine && !canEdit) {
      setError("You do not have permission to edit lines");
      return;
    }
    if (!editingLine && !canCreate) {
      setError("You do not have permission to create lines");
      return;
    }

    if (!validateForm()) {
      return;
    }

    const toastId = toast.loading(editingLine ? "Updating line..." : "Creating line...");
    try {
      setSubmitting(true);
      setError("");

      const lineData = {
        lineName: formData.lineName.trim(),
        plantId: formData.plantId ? parseInt(formData.plantId) : null,
        departmentId: formData.departmentId
          ? parseInt(formData.departmentId)
          : null,
        lineHeadId: formData.lineHeadId ? parseInt(formData.lineHeadId) : null,
        plant: null, // Empty object to satisfy backend validation
        department: null, // Empty object to satisfy backend validation
        machines: [], // Empty array to satisfy backend validation
        // Note: tenantId is set by backend from JWT token
      };

      if (editingLine) {
        // Update
        lineData.lineId = editingLine.lineId;
        await lineService.updateLine(editingLine.lineId, lineData);
      } else {
        // Create
        await lineService.createLine(lineData);
      }

      await loadData(false); // Don't show loading on refresh after save
      setShowModal(false);
      setFormData({
        lineName: "",
        plantId: "",
        departmentId: "",
        lineHeadId: "",
      });
      setEditingLine(null);
      toast.update(toastId, { render: editingLine ? "Line updated successfully!" : "Line created successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save line. Please try again.";
      setError(errorMessage);
      toast.update(toastId, { render: errorMessage, type: "error", isLoading: false, autoClose: 3000, closeButton: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLine(null);
    setFormData({
      lineName: "",
      plantId: "",
      departmentId: "",
      lineHeadId: "",
    });
    setFormErrors({});
  };

  const getPlantName = (plantId) => {
    if (!plantId) return "-";
    const plant = plants.find((p) => p.plantId === plantId);
    return plant ? plant.plantName : "-";
  };

  const getDepartmentName = (departmentId) => {
    if (!departmentId) return "-";
    const dept = departments.find((d) => d.departmentId === departmentId);
    return dept ? dept.departmentName : "-";
  };

  const getLineHeadName = (userId) => {
    if (!userId) return "-";
    // Use loose equality to handle string/number type mismatch
    const user = users.find((u) => u.userId == userId);
    return user ? user.fullName : "-";
  };

  return (
    <div className="lines-page pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h2>Lines</h2>
          <p>Manage production lines in your organization</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search lines..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="filter-group-alt">
            <select
              id="plantFilter"
              value={plantFilter}
              onChange={handlePlantFilter}
              className="form-select !min-h-[38px] !py-1.5"
            >
              <option value="">All Plants</option>
              {plants.map((plant) => (
                <option key={plant.plantId} value={plant.plantId.toString()}>
                  {plant.plantName}
                </option>
              ))}
            </select>
          </div>
          {canCreate && (
            <button className="btn-primary" onClick={handleAdd}>
              <HiOutlinePlus />
              Add Line
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="table-info">
            {loading && lines.length === 0 ? (
              <PageLoading variant="muted" message="Loading…" />
            ) : (
              `${filteredLines.length} ${filteredLines.length === 1 ? "line" : "lines"}`
            )}
          </div>
        </div>

        {loading && lines.length === 0 ? (
          <div className="table-loading">
            <PageLoading variant="compact" message="Loading lines…" />
          </div>
        ) : filteredLines.length === 0 ? (
          <div className="empty-state">
            <HiOutlineRectangleStack className="empty-icon" />
            <h3>No lines found</h3>
            <p>
              {searchTerm || plantFilter
                ? "Try adjusting your search or filter terms"
                : "Get started by creating your first line"}
            </p>
            {!searchTerm && !plantFilter && canCreate && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Line
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Line Name</th>
                <th>Plant</th>
                <th>Department</th>
                <th>Line Head</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLines.map((line, index) => (
                <tr key={line.lineId}>
                  <td>{index + 1}</td>
                  <td className="name-cell">{line.lineName}</td>
                  <td>{getPlantName(line.plantId)}</td>
                  <td>{getDepartmentName(line.departmentId)}</td>
                  <td>{getLineHeadName(line.lineHeadId)}</td>
                  <td>
                    {line.createdAt
                      ? new Date(line.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(line)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(line)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLine ? "Edit Line" : "New Line"}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="line-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingLine
                  ? "Updating..."
                  : "Creating..."
                : editingLine
                  ? "Update"
                  : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="line-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="lineName">
                Line Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lineName"
                value={formData.lineName}
                onChange={(e) =>
                  setFormData({ ...formData, lineName: e.target.value })
                }
                className={formErrors.lineName ? "error" : ""}
                placeholder="Enter line name"
                maxLength={100}
              />
              {formErrors.lineName && (
                <span className="field-error">{formErrors.lineName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lineHeadId">Line Head</label>
              <select
                id="lineHeadId"
                value={formData.lineHeadId}
                onChange={(e) =>
                  setFormData({ ...formData, lineHeadId: e.target.value })
                }
              >
                <option value="">Select Line Head (Optional)</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId.toString()}>
                    {user.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="plantId">Plant</label>
              <select
                id="plantId"
                value={formData.plantId}
                onChange={(e) =>
                  setFormData({ ...formData, plantId: e.target.value })
                }
              >
                <option value="">Select Plant (Optional)</option>
                {plants.map((plant) => (
                  <option
                    key={plant.plantId}
                    value={plant.plantId.toString()}
                  >
                    {plant.plantName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="departmentId">Department</label>
              <select
                id="departmentId"
                value={formData.departmentId}
                onChange={(e) =>
                  setFormData({ ...formData, departmentId: e.target.value })
                }
              >
                <option value="">Select Department (Optional)</option>
                {departments.map((dept) => (
                  <option
                    key={dept.departmentId}
                    value={dept.departmentId.toString()}
                  >
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm?.lineId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.lineName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. This line will be permanently
            deleted.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Lines;
